import { useCallback, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Check,
  EquipUIFireflyV2Theme,
  FAIconName,
  Modal,
  SkeletonLoader,
  useToastMessage,
} from '@equip.health/ui';

import ModalFooterContent from '~/components/common/calendar/ModalFooterContent';
import ProviderSelect from '~/components/common/calendar/ProviderSelect';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getUserOption } from '~/lib/util/user.util';
import {
  useAddHostsMutation,
  useUpdateGroupSectionEventV2Mutation,
} from '~/store/services/groupClass';
import { useLazyGetAllProvidersV2Query } from '~/store/services/providers';
import { GLOBAL_GENERIC_ERROR_TEXT } from '~/lib/constants/global.constants';

const TOP_LEVEL_ID = 'add-hosts-modal';

const GET_ALL_PROVIDERS_PAGE_SIZE = 10;
const {
  neutral: {
    secondary: neutralSecondary,
    background: { primary: neutralBgPrimary },
  },
} = EquipUIFireflyV2Theme.colors;

interface AddHostsModalProps {
  groupClassEventExternalId: string;
  hostIds: string[];
  isOpen: boolean;
  onClose: (hosts?: AutoCompleteOption[]) => void;
  useGroupSectionEventV2?: boolean;
}

const AddHostsModal = ({
  groupClassEventExternalId,
  hostIds: defaultHostIds = [],
  isOpen,
  onClose,
  useGroupSectionEventV2 = false,
}: AddHostsModalProps) => {
  const [newHosts, setNewHosts] = useState<AutoCompleteOption[]>([]);
  const [providerSearchPageNumber, setProviderSearchPageNumber] =
    useState<number>(1);
  const [providerSearchTotalCount, setProviderSearchTotalCount] =
    useState<number>(0);
  const [providerSearchResults, setProviderSearchResults] = useState<
    GetProviderByIdResponse[]
  >([]);

  const { calendarAccount, currentUser, isCalendarAccountLoading } =
    useGlobalContext();

  const [getAllProviders, { isFetching: isGetAllProvidersFetching }] =
    useLazyGetAllProvidersV2Query();
  const [addHosts] = useAddHostsMutation();
  const [updateGroupSectionEventV2] = useUpdateGroupSectionEventV2Mutation();
  const { createToast } = useToastMessage();

  const providerOptions = useMemo<AutoCompleteOption[]>(() => {
    if (!calendarAccount) {
      return [];
    }
    return [
      {
        ...getUserOption(
          true,
          true,
        )({
          ...currentUser,
          calendarId: calendarAccount.calendarId,
        }),
        isMe: true,
      } as AutoCompleteOption,
    ]
      .concat(
        (providerSearchResults ?? [])
          .filter(({ userId }) => userId !== currentUser.userId)
          .map(getUserOption(true, true)),
      )
      .map((option: AutoCompleteOption) => {
        return defaultHostIds.includes(option.value)
          ? {
              ...option,
              isDisabled: true,
            }
          : option;
      });
  }, [currentUser, providerSearchResults, calendarAccount]);

  const handleProviderRemove = (option: AutoCompleteOption): void =>
    setNewHosts((prev) =>
      prev.filter((host: AutoCompleteOption) => host.value !== option.value),
    );

  const handleProviderSelect = (providerOption: AutoCompleteOption): void =>
    setNewHosts((prev) => [...prev, providerOption]);

  const handleCancel = (): void => {
    setNewHosts([]);
    onClose();
  };

  const handleProviderSearch = useCallback(
    (searchTerm = '', isFirstPage = true): void => {
      const newPageNumber = isFirstPage ? 1 : providerSearchPageNumber + 1;

      getAllProviders({
        'include-calendar-profile': true,
        'page-number': newPageNumber,
        'page-size': GET_ALL_PROVIDERS_PAGE_SIZE,
        'provider-search': searchTerm,
        status: 'active',
      })
        .unwrap()
        .then((result) => {
          const { data, count } = result ?? {};
          if (data) {
            setProviderSearchResults((curr) =>
              newPageNumber === 1 ? data : [...curr, ...data],
            );
            setProviderSearchTotalCount(count);
            setProviderSearchPageNumber(newPageNumber);
          }
        });
    },
    [providerSearchPageNumber],
  );

  const onConfirm = (): void => {
    const hostExternalIds = [
      ...defaultHostIds,
      ...newHosts.map((host: AutoCompleteOption) => host.value),
    ];

    const request = useGroupSectionEventV2
      ? updateGroupSectionEventV2({
          groupEventExternalId: groupClassEventExternalId,
          hosts: hostExternalIds,
          isCancel: false,
          isDecline: false,
        })
      : addHosts({
          groupClassEventExternalId,
          hostExternalIds,
        });

    request
      .unwrap()
      .then(() => {
        onClose(newHosts);
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}__add-hosts-error`,
          text: GLOBAL_GENERIC_ERROR_TEXT,
          variant: 'critical',
        });
      });
  };

  return (
    <Modal
      bodyStyle={{ overflow: 'visible' }}
      closeBtnStyle={{ color: neutralSecondary }}
      footerContent={
        <ModalFooterContent
          handleLeftButtonClick={handleCancel}
          handleRightButtonClick={onConfirm}
          isRightButtonDisabled={newHosts.length === 0}
          leftButtonText="Cancel"
          rightButtonIcon={
            <Check fill={neutralBgPrimary} height="17px" width="16px" />
          }
          rightButtonText="Confirm"
        />
      }
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={handleCancel}
      size="2xl"
      title="Add hosts"
      titleStyle={{
        fontSize: '20px',
      }}
    >
      <ChakraFlex flexDirection="column">
        {isCalendarAccountLoading && <SkeletonLoader id="add-hosts-modal" />}
        {!isCalendarAccountLoading && (
          <>
            <ChakraText id={`${TOP_LEVEL_ID}__text`} paddingY="24px">
              Please select additional host(s) for this event
            </ChakraText>
            <ChakraBox width="100%">
              <ProviderSelect
                handleProviderRemove={handleProviderRemove}
                handleProviderSearch={handleProviderSearch}
                handleProviderSelect={handleProviderSelect}
                height="48px"
                isDisabled={isGetAllProvidersFetching}
                isLoading={isGetAllProvidersFetching}
                multiple
                options={providerOptions}
                selectedProvider={newHosts}
                total={
                  providerSearchTotalCount +
                  (providerOptions.length - providerSearchResults.length)
                }
              />
            </ChakraBox>
          </>
        )}
      </ChakraFlex>
    </Modal>
  );
};

export default AddHostsModal;
